import { Stack, Divider, Button, Switch, Paper, Box } from "@mui/material";
import { TableFilter } from "@hooks/use-table-context/types";
import { DataTableLabels, TableCellInputType } from "../../../../components/data-table/types";
import { Fragment } from "react/jsx-runtime";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { underscoreToTitleCase } from "@utils/index";

type FilterOption = string | { value: string; label: string };

// Config-driven select/taxonomy filters store the raw option value (e.g.
// "in_repair") — look up its configured label so chips read like the UI
// they were selected from. If unmatched, auto title-case (with acronym
// handling, e.g. "it_local_administrator" -> "IT Local Administrator")
// rather than showing the raw slug.
const resolveOptionLabel = <T extends string | boolean | number | null | undefined>(
  value: T,
  options?: FilterOption[]
): T | string => {
  if (value == null || !options?.length) return value;
  const match = options.find((option) =>
    typeof option === "string" ? option === value : String(option.value) === String(value)
  );
  if (match) return typeof match === "string" ? match : match.label;
  // Select/taxonomy-backed field with no matching option label (e.g. a
  // stale/incomplete options list) — fall back to auto title-case instead
  // of the raw slug. Plain text filters (no `options`) are left untouched.
  return typeof value === "string" ? underscoreToTitleCase(value) : value;
};

export const FilterValue = ({
  value,
  endValue,
  inputType,
  displayType,
  options,
}: {
  value: string | boolean | null | number;
  endValue?: string | null | number;
  inputType: TableCellInputType;
  displayType?: TableCellInputType;
  options?: FilterOption[];
}) => {
  if (inputType === TableCellInputType.Boolean || displayType === TableCellInputType.Boolean) {
    const isTrue = value === true || value === "1" || value === 1 || value === "true";
    return isTrue ? <CheckIcon color="success" /> : <CloseIcon color="error" />;
  }

  const resolvedValue = resolveOptionLabel(value, options);
  const resolvedEndValue = resolveOptionLabel(endValue, options);

  // if value and endValue
  if (endValue !== undefined && endValue !== null && value !== null && value !== undefined) {
    return (
      <span>
        {resolvedValue} - {resolvedEndValue}
      </span>
    );
  }

  // if no value and endValue
  if ( (value === null || value === undefined) && (endValue !== null && endValue !== undefined)) {
    return <span>{resolvedEndValue}</span>;
  }

  // if value
  return <span>{resolvedValue}</span>;
};

export const CurrentFiltersList = ({
  filters,
  filtersToRemove,
  removeAllFilters,
  handleUpdateFilters,
  setFiltersToRemove,
  keyLabels,
  tableCellInputConfig
}: {
  filters: TableFilter<any>[];
  filtersToRemove: TableFilter<any>[];
  removeAllFilters: () => void;
  handleUpdateFilters: () => void;
  setFiltersToRemove: (filters: any) => void;
  keyLabels: DataTableLabels;
  tableCellInputConfig?: {
    [key: string]: {
      type: TableCellInputType;
      options?: (string | { value: string; label: string })[];
    };
  };
}) => {
  return (
    <>
      {filters?.length > 0 && (
        <>
          <Box sx={{ mb: 0, pb: 1, pl: 0.5 }} component="p">
            Current Filters:
          </Box>
          <Divider sx={{ mb: 2 }} />
          {filters.map((filter) => (
            <Fragment key={filter.id}>
              <Paper
                elevation={
                  !!!filtersToRemove.find((f) => f.id === filter.id) ? 2 : 0
                }
                sx={{ "&.MuiPaper-root": { p: 0.5 } }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <b
                      style={{
                        marginRight: "0.5rem",
                      }}
                    >
                      {keyLabels?.[String(filter.column)] ?? filter.column}{" "}
                      {tableCellInputConfig?.[String(filter.column)]?.type === 'date' ? (
                        filter.endValue && !filter.value ? 'before' :
                        filter.value && !filter.endValue ? 'after' :
                        filter.value && filter.endValue ? 'between' :
                        filter.type
                      ) : (
                        filter.type
                      )}:
                    </b>

                    <FilterValue
                      value={filter.value}
                      endValue={typeof filter.endValue === 'boolean' ? null : (filter.endValue || null)}
                      inputType={
                        (filter.inputType as TableCellInputType) ??
                        TableCellInputType.Text
                      }
                      options={tableCellInputConfig?.[String(filter.column)]?.options}
                    />
                  </span>

                  <Switch
                    size="medium"
                    color={"success"}
                    checked={!!!filtersToRemove.find((f) => f.id === filter.id)}
                    onChange={() =>
                      !!filtersToRemove.find((f) => f.id === filter.id)
                        ? setFiltersToRemove(
                            filtersToRemove.filter((f) => f.id !== filter.id)
                          )
                        : setFiltersToRemove([...filtersToRemove, filter])
                    }
                  />
                </Stack>
              </Paper>
              <Divider />
            </Fragment>
          ))}
        </>
      )}
      {filters.length > 0 && (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
          sx={{ mt: 2.5 }}
        >
          <Button
            sx={{ flexGrow: 1 }}
            variant="outlined"
            onClick={removeAllFilters}
          >
            Remove All Filters
          </Button>
          <Button
            sx={{ flexGrow: 1 }}
            variant="contained"
            disabled={filtersToRemove.length === 0}
            onClick={handleUpdateFilters}
          >
            Update Filters
          </Button>
        </Stack>
      )}
    </>
  );
};

export default CurrentFiltersList;